import React from 'react';
import { useTranslation } from 'react-i18next';

import { GATE2WAY_PROVIDER } from '@/utils/session-invoice';
import { FINISH } from '../../finish.tokens';
import { CheckoutBody, type CheckoutBodyProps } from './CheckoutBody';
import { Root, Header, BackButton, BackLabel, HeaderTitle, Body } from './PaymentPane.styles';

export type CheckoutPaneProps = CheckoutBodyProps & {
  /** Leave the checkout entirely (back to the review step). */
  onBack: () => void;
};

const BackGlyph = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke={FINISH.backIconStroke} strokeWidth="1.5" />
    <path
      d="M13.5 8.5 10 12l3.5 3.5"
      stroke={FINISH.backIconStroke}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Desktop checkout pane. Same shell as the Stripe `PaymentPane` it replaces (a "‹ Back" header
 * over the checkout body, rendered as the WizardLayout `overlay`), with the body running the
 * Mexican checkout: choose a rail and pay by card, or take the bank coordinates and transfer.
 *
 * Back is contextual: from the coordinates it returns to the form (so the buyer can correct a
 * name without losing the step), and from the form it leaves the checkout.
 */
export const CheckoutPane = ({ onBack, ...body }: CheckoutPaneProps): React.ReactElement => {
  const { t } = useTranslation();
  const { checkout } = body;
  const onInstructions = checkout.view === 'instructions' && checkout.details;

  return (
    <Root>
      <Header>
        <BackButton
          type="button"
          data-ga-id="cfg-mx-payment-back"
          onClick={onInstructions ? checkout.backToForm : onBack}
        >
          {BackGlyph}
          <BackLabel text="Back" tag="span" />
        </BackButton>
        <HeaderTitle text={onInstructions ? (checkout.method === GATE2WAY_PROVIDER ? t('Pix') : t('Bank transfer')) : t('Finalize payment')} tag="span" />
      </Header>

      <Body>
        <CheckoutBody {...body} />
      </Body>
    </Root>
  );
};

export default CheckoutPane;
